<script setup lang="ts">
import { computed, ref } from 'vue'

const props = defineProps<{
  items: Record<string, any>[]
  skeleton?: boolean
}>()

const expandedRow = ref<number | null>(null)

function toggleRow(index: number) {
  expandedRow.value = expandedRow.value === index ? null : index
}

const columns = computed(() => {
  if (!props.items.length) return []
  return Object.keys(props.items[0]).filter((k) => k !== 'debug')
})

function formatHeader(key: string) {
  const cashPositionMatch = key.match(/^cash_position_(\d{4})$/)
  if (cashPositionMatch) return `Cash Position for ${cashPositionMatch[1]}`
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}
</script>

<template>
  <div class="overflow-hidden rounded-lg bg-white shadow">
    <template v-if="skeleton">
      <div class="animate-pulse p-6">
        <div class="mb-4 h-4 w-48 rounded bg-gray-200"></div>
        <div v-for="i in 5" :key="i" class="mb-2 h-8 rounded bg-gray-100"></div>
      </div>
    </template>
    <template v-else>
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th
              v-for="col in columns"
              :key="col"
              class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
            >
              {{ formatHeader(col) }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200 bg-white">
          <template v-for="(item, index) in items" :key="item.id || index">
            <tr
              :class="item.debug ? 'cursor-pointer hover:bg-gray-50' : ''"
              @click="item.debug ? toggleRow(index) : null"
            >
              <td v-for="col in columns" :key="col" class="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                <span v-if="col === columns[0] && item.debug" class="mr-1 text-gray-400">{{
                  expandedRow === index ? '▼' : '▶'
                }}</span>
                {{ item[col] }}
              </td>
            </tr>
            <!-- Debug detail row -->
            <tr v-if="item.debug && expandedRow === index">
              <td colspan="3" class="bg-gray-50 px-6 py-4">
                <div class="space-y-3">
                  <template v-for="(value, key) in item.debug" :key="key">
                    <div class="rounded-md border border-gray-200 bg-white p-3">
                      <p class="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        {{ String(key).replace(/_/g, ' ') }}
                      </p>
                      <div v-if="Array.isArray(value)" class="overflow-x-auto">
                        <template v-if="value.length === 0">
                          <p class="text-xs text-gray-400">No records</p>
                        </template>
                        <table v-else-if="typeof value[0] === 'object' && value[0] !== null" class="min-w-full text-xs">
                          <thead>
                            <tr>
                              <th
                                v-for="col in Object.keys(value[0])"
                                :key="col"
                                class="pb-1 pr-4 text-left font-medium text-gray-500"
                              >
                                {{ col }}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(row, idx) in value" :key="idx" class="border-t border-gray-200">
                              <td
                                v-for="col in Object.keys(value[0])"
                                :key="col"
                                class="whitespace-nowrap py-1 pr-4 text-gray-700"
                              >
                                {{ row[col] }}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                        <p v-else class="break-all font-mono text-xs text-gray-700">
                          {{ value.join(', ') }}
                        </p>
                      </div>
                      <div v-else-if="typeof value === 'object' && value !== null">
                        <div v-for="(v, k) in value" :key="k" class="text-xs text-gray-700">
                          <span class="text-gray-500">{{ k }}:</span>
                          <span class="ml-1 font-mono">{{ v }}</span>
                        </div>
                      </div>
                      <p v-else class="font-mono text-xs text-gray-700">{{ value }}</p>
                    </div>
                  </template>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </template>
  </div>
</template>
